import { API_TYPES, WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { groupBy, isEmpty } from "lodash";

import { useLegacyStateContext } from "../LegacyStateBridge/context";
import { useGetWorkerRequirements } from "../redesign/Documents/api/useGetWorkerRequirements";
import { useGetWorker } from "./api/useGetWorker";

export interface WorkerDocumentsByStatus {
  approvedDocuments: string[];
  pendingDocuments: string[];
  rejectedDocuments: string[];
  expiredDocuments: string[];
  expiringDocuments: string[];
}

// Provide worker document names grouped by status.
export function useWorkerDocumentsByStatus(): WorkerDocumentsByStatus {
  const legacyState = useLegacyStateContext();
  const isAuthorized = legacyState?.session?.isAuthorized ?? false;

  const { data: worker, isSuccess: workerIsLoaded } = useGetWorker({
    enabled: isAuthorized,
  });

  const availableLicenses = isDefined(worker)
    ? worker.licensesData.map((license) => license.qualification)
    : [];

  const queryParams = {
    filter: {
      qualifications: availableLicenses,
    },
    include: [API_TYPES.requirements],
  };

  const { data: requirements = [] } = useGetWorkerRequirements(queryParams, {
    enabled: isDefined(worker?.userId) && workerIsLoaded,
  });

  if (isEmpty(requirements)) {
    return {
      approvedDocuments: [],
      pendingDocuments: [],
      rejectedDocuments: [],
      expiredDocuments: [],
      expiringDocuments: [],
    };
  }

  const groupedRequirements = groupBy(requirements, "status");

  const getDocumentNames = (status: WorkerRequirementStatus) =>
    groupedRequirements[status]?.map(
      (request) => request.requirement?.attributes?.name ?? request.requirementId
    ) ?? [];

  return {
    approvedDocuments: getDocumentNames(WorkerRequirementStatus.APPROVED),
    pendingDocuments: getDocumentNames(WorkerRequirementStatus.IN_REVIEW),
    rejectedDocuments: getDocumentNames(WorkerRequirementStatus.REJECTED),
    expiredDocuments: getDocumentNames(WorkerRequirementStatus.EXPIRED),
    expiringDocuments: getDocumentNames(WorkerRequirementStatus.EXPIRING),
  };
}
